import { PartImageIdentification, PartImageIdentificationStage } from '@/api/generatedApi';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';

const STEPS = ['Uploading photos', 'Reading the photos', 'Zooming into labels', 'Matching FLIS'];
const UPLOADING = 0;
const READING = 1;

const STAGE_STEPS: Record<PartImageIdentificationStage, number> = {
  [PartImageIdentificationStage.ReadingPhotos]: READING,
  [PartImageIdentificationStage.ZoomingLabels]: 2,
  [PartImageIdentificationStage.MatchingFlis]: 3,
  [PartImageIdentificationStage.Finished]: STEPS.length,
};

export const identificationStep = (identificationId?: string, identification?: PartImageIdentification): number => {
  if (identification) return STAGE_STEPS[identification.stage];
  return identificationId ? READING : UPLOADING;
};

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.7); }
`;

const PulsingDot = styled(Box)(({ theme }) => ({
  width: 12,
  height: 12,
  margin: 4,
  borderRadius: '50%',
  backgroundColor: theme.palette.info.main,
  boxShadow: `0 0 0 4px ${alpha(theme.palette.info.main, 0.2)}`,
  animation: `${pulse} 1.4s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}));

const StepIcon = ({ done, current }: { done: boolean; current: boolean }) => {
  if (done) return <CheckCircle color='success' sx={{ fontSize: 20 }} />;
  if (current) return <PulsingDot />;
  return <RadioButtonUnchecked color='disabled' sx={{ fontSize: 20 }} />;
};

const stepColor = (done: boolean, current: boolean): string => {
  if (current) return 'text.primary';
  return done ? 'text.secondary' : 'text.disabled';
};

export const IdentificationProgress = ({ step }: { step: number }) => (
  <Stack
    direction='row'
    flexWrap='wrap'
    columnGap='20px'
    rowGap='8px'
    alignItems='center'
    data-testid='part-image-identifying'
  >
    {STEPS.map((label, index) => {
      const done = index < step;
      const current = index === step;
      return (
        <Stack key={label} direction='row' gap='6px' alignItems='center' aria-current={current ? 'step' : undefined}>
          <StepIcon done={done} current={current} />
          <Typography variant='body2' color={stepColor(done, current)} fontWeight={current ? 500 : 400}>
            {label}
          </Typography>
        </Stack>
      );
    })}
  </Stack>
);
